import type { StorefrontLocaleConfig } from '@vastra/types';

/** Default storefront language (always available; not listed in additional locales). */
export const DEFAULT_STOREFRONT_LOCALE = 'en';

/** Used when site settings omit `additionalStorefrontLocales` (legacy stores). */
export const DEFAULT_ADDITIONAL_STOREFRONT_LOCALES: StorefrontLocaleConfig[] = [
  { code: 'ta', label: 'Tamil', nativeLabel: 'தமிழ்' },
];

const LOCALE_CODE_RE = /^[a-z]{2,3}(-[a-z]{2})?$/;

export function normalizeStorefrontLocaleCode(raw: string): string {
  return raw.trim().toLowerCase().replace(/_/g, '-');
}

export function isValidAdditionalStorefrontLocaleCode(code: string): boolean {
  return LOCALE_CODE_RE.test(code) && code !== DEFAULT_STOREFRONT_LOCALE;
}

/** Parse admin/API payload; returns only valid non-English locales (max 20). */
export function parseStorefrontLocaleList(raw: unknown): StorefrontLocaleConfig[] {
  if (!Array.isArray(raw)) return [];
  const out: StorefrontLocaleConfig[] = [];
  const seen = new Set<string>([DEFAULT_STOREFRONT_LOCALE]);
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const code = normalizeStorefrontLocaleCode(String(o.code ?? ''));
    if (!isValidAdditionalStorefrontLocaleCode(code) || seen.has(code)) continue;
    const label = String(o.label ?? '').trim();
    if (!label) continue;
    seen.add(code);
    const nativeLabel = typeof o.nativeLabel === 'string' ? o.nativeLabel.trim() : '';
    out.push(nativeLabel ? { code, label, nativeLabel } : { code, label });
  }
  return out.slice(0, 20);
}

export function resolveAdditionalStorefrontLocales(
  configured: StorefrontLocaleConfig[] | undefined,
): StorefrontLocaleConfig[] {
  if (configured === undefined) return [...DEFAULT_ADDITIONAL_STOREFRONT_LOCALES];
  return configured;
}

/** English first, then configured additional locales. */
export function getStorefrontLocaleOptions(
  additional: StorefrontLocaleConfig[] | undefined,
): StorefrontLocaleConfig[] {
  const extras = resolveAdditionalStorefrontLocales(additional).filter((l) => l.code !== DEFAULT_STOREFRONT_LOCALE);
  return [{ code: DEFAULT_STOREFRONT_LOCALE, label: 'English', nativeLabel: 'English' }, ...extras];
}

export function getStorefrontLocaleCodes(additional: StorefrontLocaleConfig[] | undefined): string[] {
  return getStorefrontLocaleOptions(additional).map((l) => l.code);
}

export function localeSwitcherShortLabel(config: StorefrontLocaleConfig): string {
  const base = config.code.split('-')[0] ?? config.code;
  return base.slice(0, 3).toUpperCase();
}
